import React, { useEffect, useState, useRef } from "react";
import { OctagonX, CheckCircle2, Info, TriangleAlert } from "lucide-react";

// Funzione per calcolare la durata in base al testo
const calculateAlertDuration = (text) => {
    const wordCount = text.trim().split(/\s+/).length;

    if (wordCount <= 3) return 2000;     // Messaggi molto brevi: 2s
    if (wordCount <= 8) return 3500;     // Messaggi brevi: 3.5s  
    if (wordCount <= 15) return 5000;    // Messaggi medi: 5s
    return 6500;                         // Messaggi lunghi: 6.5s
};

const Alert = ({ type, text, onClose = () => { }, needConfirm = false, onConfirm = () => { } }) => {
    const [isVisible, setIsVisible] = useState(true);
    const [isExiting, setIsExiting] = useState(false);
    const [isShaking, setIsShaking] = useState(false);
    const alertRef = useRef(null);
    // Calcola la durata in base al testo
    const duration = calculateAlertDuration(text);

    useEffect(() => {
        // Animazione di entrata
        const timerIn = setTimeout(() => {
            setIsVisible(true);
        }, 10);

        // Animazione di uscita dopo la durata specificata
        if (duration > 0 && !needConfirm) {
            const timerOut = setTimeout(() => {
                startExitAnimation();
            }, duration - 300); // Sottraiamo 300ms per completare l'animazione prima della rimozione

            return () => {
                clearTimeout(timerIn);
                clearTimeout(timerOut);
            };
        }
    }, [duration]);

    // Gestione del click outside quando needConfirm è true
    useEffect(() => {
        if (!needConfirm) return;

        const handleClickOutside = (event) => {
            if (alertRef.current && !alertRef.current.contains(event.target)) {
                // Blocca il click al di fuori dell'alert
                event.stopPropagation();
                event.preventDefault();

                setIsShaking(true);
                setTimeout(() => setIsShaking(false), 600); // Durata dell'animazione di shake
            }
        };

        // Aggiungi l'event listener per mousedown e click
        document.addEventListener('mousedown', handleClickOutside, true);
        document.addEventListener('click', handleClickOutside, true);
        document.addEventListener('touchstart', handleClickOutside, true);

        // Disabilita lo scroll del body
        document.body.style.overflow = 'hidden';

        return () => {
            document.removeEventListener('mousedown', handleClickOutside, true);
            document.removeEventListener('click', handleClickOutside, true);
            document.removeEventListener('touchstart', handleClickOutside, true);
            document.body.style.overflow = 'unset';
        };
    }, [needConfirm]);

    const startExitAnimation = () => {
        setIsExiting(true);
        setTimeout(() => {
            onClose?.();
        }, 300); // Durata dell'animazione di uscita
    };

    const alertConfig = {
        info: {
            icon: <Info size={16} />,
            classes:
                "border-2 border-blue-300 bg-blue-100 text-blue-800 dark:border-blue-700 dark:bg-blue-900 dark:text-blue-200",
            buttonConfirm:
                "bg-blue-800 hover:bg-blue-700 text-blue-300 dark:bg-blue-200 dark:hover:bg-blue-300 dark:text-blue-700",
            buttonCancel:
                "border-2 border-blue-800 text-blue-800 hover:bg-blue-200 dark:border-blue-200 dark:text-blue-200 dark:hover:bg-blue-800",
        },
        success: {
            icon: <CheckCircle2 size={16} />,
            classes:
                "border-2 border-green-300 bg-green-100 text-green-800 dark:border-green-700 dark:bg-green-900 dark:text-green-200",
            buttonConfirm:
                "bg-green-800 hover:bg-green-700 text-green-300 dark:bg-green-200 dark:hover:bg-green-300 dark:text-green-700",
            buttonCancel:
                "border-2 border-green-800 text-green-800 hover:bg-green-200 dark:border-green-200 dark:text-green-200 dark:hover:bg-green-800",
        },
        warning: {
            icon: <TriangleAlert size={16} />,
            classes:
                "border-2 border-yellow-300 bg-yellow-100 text-yellow-800 dark:border-yellow-700 dark:bg-yellow-900 dark:text-yellow-200",
            buttonConfirm:
                "bg-yellow-800 hover:bg-yellow-700 text-yellow-300 dark:bg-yellow-200 dark:hover:bg-yellow-300 dark:text-yellow-700",
            buttonCancel:
                "border-2 border-yellow-800 text-yellow-800 hover:bg-yellow-200 dark:border-yellow-200 dark:text-yellow-200 dark:hover:bg-yellow-800",
        },
        error: {
            icon: <OctagonX size={16} />,
            classes:
                "border-2 border-red-300 bg-red-100 text-red-800 dark:border-red-700 dark:bg-red-900 dark:text-red-200",
            buttonConfirm:
                "bg-red-800 hover:bg-red-700 text-red-300 dark:bg-red-200 dark:hover:bg-red-300 dark:text-red-700",
            buttonCancel:
                "border-2 border-red-800 text-red-800 hover:bg-red-200 dark:border-red-200 dark:text-red-200 dark:hover:bg-red-800",
        }
    };

    const { icon, classes, buttonConfirm, buttonCancel } = alertConfig[type] || alertConfig.info;

    return (
        <>
            {/* Overlay di sfondo quando needConfirm è true */}
            {needConfirm && (
                <div className="fixed inset-0 bg-black bg-opacity-0 z-[9999]" />
            )}
            <div
                ref={alertRef}
                className={`fixed top-4 left-1/2 transform -translate-x-1/2 z-[10000] w-full max-w-xs sm:max-w-md transition-all duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] 
                    ${isVisible ? (isExiting ? 'opacity-0 -translate-y-2' : 'opacity-100 translate-y-0') : 'opacity-0 -translate-y-2'}
                    ${isShaking ? 'animate-shake' : ''}`}
            >
                <div
                    className={`flex flex-col p-4 text-sm border rounded-lg shadow-lg ${classes}`}
                    role="alert"
                >
                    <div className="flex items-center">
                        <span className="shrink-0 inline-flex mr-3">{icon}</span>
                        <div className="flex-1 font-semibold">{text}</div>
                    </div>
                    {
                        needConfirm && (
                            <div className="flex justify-end mt-2">
                                <button
                                    onClick={() => {
                                        startExitAnimation();
                                    }}
                                    className={`px-3 py-1 rounded-lg ${buttonCancel} active:scale-95 transition-all`}
                                >ANNULLA</button>
                                <button
                                    onClick={() => {
                                        onConfirm?.();
                                        startExitAnimation();
                                    }}
                                    className={`px-3 py-1 ml-2 rounded-lg ${buttonConfirm} active:scale-95 transition-all`}
                                >CONFERMA</button>
                            </div>
                        )
                    }
                </div>
            </div>
        </>
    );
};

export default Alert;